import React, { ChangeEvent, useEffect, useState, KeyboardEvent } from "react";
import { FieldArray, FieldArrayRenderProps, FormikProps } from "formik";
import TextField from "../../../../../shared/components/forms/TextField";
import Button from "../../../../../shared/components/general/Button";
import styled from "@emotion/styled";
import { css } from "@emotion/css";
import StyledDropdown from "../../../../../shared/scenes/applicationSettings/components/customAssistantConfigurations/StyledDropdown";
import { getAIAnsweringModuleId, getFoodOrderingModuleId, getHealthcareSchedulingModuleId } from "../../../../../shared/constants/ModuleConsts";
import { getAvailableTemplateConfigurations } from "../../../../../shared/api/controllers/templateConfigurations";
import { AIAnsweringFeatureFlagId, FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from "../../../../../shared/constants/featureFlagIds";
import { getDefaultFormFieldsByAssistantType } from "../../../../../shared/constants/sparkConstants/defaultFormFieldsByAssistantType";
import {
  color_shades_darker,
  color_shades_dark,
  silver_two,
  text_blue,
  dark_grey_blue,
  color_shades_darkest,
} from "../../../../../shared/constants/colors";
import CheckboxField from "../../../../../shared/components/forms/CheckboxField";
import ApplicationModel from "../../../../../shared/models/applications/api/ApplicationModel";
import TemplateFormSectionModel from "../../../../../shared/models/templating/api/TemplateFormSectionModel";
import TemplateFormFieldModel from "../../../../../shared/models/templating/api/TemplateFormFieldModel";
import OrganizationContainer from "../../../../../shared/state/containers/OrganizationContainer";
import ApplicationContainer from "../../../../../shared/state/containers/ApplicationContainer";
import CollapsingPanel from "../../../../../shared/components/structure/Panels/CollapsingPanel";
import VerticalSeparator from "../../../../../shared/components/structure/VerticalSeparator";
import SearchField from "../../../../../shared/components/forms/SearchField";
import { breakpoint_small } from "../../../../../shared/constants/breakpoints";
import PageError from "../../../../../shared/components/general/PageError";
import SecondaryLoader from "../../../../../shared/components/general/SecondaryLoader";

const deleteIcon = require("../../../../../shared/content/images/custom-assistant-deployment/delete.svg");
const xIcon = require("../../../../../shared/content/images/x-small.svg");
const expandIcon = require("../../../../../shared/content/images/expand-circle.svg");
const collapseIcon = require("../../../../../shared/content/images/collapse-circle.svg");
const checkIcon = require("../../../../../shared/content/images/dark-check.svg");
const searchIcon = require("../../../../../shared/content/images/search-blue.svg");
const rightArrow = require("../../../../../shared/content/images/arrow-circle-right.svg");
const lightRightArrow = require("../../../../../shared/content/images/arrow-circle-right-light.svg");
const leftArrow = require("../../../../../shared/content/images/arrow-circle-left.svg");
const lightLeftArrow = require("../../../../../shared/content/images/arrow-circle-left-light.svg");

const WAIT_INTERVAL = 800;

export interface ManageSparkLocationsFormProps {
  appContainer: ApplicationContainer;
  orgContainer: OrganizationContainer;
  commonApp: ApplicationModel;
  locationApps: ApplicationModel[];
  locationAppNames: string[];
  locationAppFormFields: TemplateFormFieldModel[];
  commonAppSections: TemplateFormSectionModel[];
  isLoadingTemplates: boolean;
  history: any;
  isSubmittingForm: boolean;
  setIsSubmittingForm: (isSubmittingForm: boolean) => void;
  padding?: string;
}

export interface ManageSparkLocationsFormData {
  commonAppName?: string;
  locationNames: string[];
  selectedFormFields: TemplateFormFieldModel[];
  deselectedFormFields: TemplateFormFieldModel[];
  assistantType: string;
  pmsName?: string;
}

const SparkLocationsForm: React.FC<ManageSparkLocationsFormProps & FormikProps<ManageSparkLocationsFormData>> = (props) => {

  const [activeIndex, setActiveIndex] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLocationCollapsed, setIsLocationCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredSections, setFilteredSections] = useState(props.commonAppSections);
  const [originalSelectedFormFields, setOriginalSelectedFormFields] = useState<TemplateFormFieldModel[]>();
  const [isAssistantTypeDisabled, setIsAssistantTypeDisabled] = useState(false);
  const [submissionErrors, setSubmissionErrors] = useState<string[]>([]);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  let timer: NodeJS.Timer = null;

  const assistantTypeOptions = [
    { value: 'ai-answering', label: 'AI Answering' },
    { value: 'ai-ordering', label: 'AI Ordering' },
    { value: 'dental-scheduling', label: 'Dental Scheduling' }
  ];

  const pmsNameOptions = [
    { value: 'cloud-9', label: 'Cloud 9' },
    { value: 'denticon', label: 'Denticon' },
    { value: 'dentrix-ascend', label: 'Dentrix Ascend' }
  ];

  // Function to determine assistant type from existing apps
  const determineAssistantTypeFromApps = () => {
    // If there's a common app present, disable the dropdown
    if (props.commonApp) {
      setIsAssistantTypeDisabled(true);

      // Check the common app for feature flags to determine type
      if (props.commonApp.featureFlags) {
        const featureFlags = props.commonApp.featureFlags;

        if (featureFlags.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)) {
          return 'dental-scheduling';
        } else if (featureFlags.some(flag => flag.id === FoodOrderingFeatureFlagId)) {
          return 'ai-ordering';
        } else if (featureFlags.some(flag => flag.id === AIAnsweringFeatureFlagId)) {
          return 'ai-answering';
        }
      }

      // Common app exists but no matching feature flags found
      return null;
    }

    // No common app, dropdown should be enabled
    setIsAssistantTypeDisabled(false);
    return null;
  };

  // Function to get sections based on selected assistant type
  const getSectionsForAssistantType = async (assistantType: string) => {
    if (!assistantType) return [];

    try {
      const availableTemplateConfigsResponse = await getAvailableTemplateConfigurations(props.orgContainer.state.currentOrganization.id);

      let selectedModuleId: string;
      switch (assistantType) {
        case 'ai-answering':
          selectedModuleId = getAIAnsweringModuleId();
          break;
        case 'ai-ordering':
          selectedModuleId = getFoodOrderingModuleId();
          break;
        case 'dental-scheduling':
          selectedModuleId = getHealthcareSchedulingModuleId();
          break;
        default:
          selectedModuleId = getAIAnsweringModuleId();
      }

      const selectedModuleConfig = availableTemplateConfigsResponse.data.find(config => config.id === selectedModuleId);
      const sections = selectedModuleConfig?.templateForm?.templateFormSections || [];

      return sections;
    } catch (error) {
      console.error('Error loading sections for assistant type:', error);
      return [];
    }
  };

  useEffect(() => {
    setOriginalSelectedFormFields(props.values.selectedFormFields);
  }, [])

  // Effect to determine assistant type based on existing apps
  useEffect(() => {
    const determinedType = determineAssistantTypeFromApps();
    if (determinedType && !props.values.assistantType) {
      props.setFieldValue("assistantType", determinedType);
    }
  }, [props.locationApps, props.commonApp])

  // Update filtered sections when assistant type changes
  useEffect(() => {
    const updateSectionsForAssistantType = async () => {
      if (props.values.assistantType) {
        const newSections = await getSectionsForAssistantType(props.values.assistantType);
        setFilteredSections(newSections);

        // Apply default form fields for the selected assistant type if:
        // 1. There are no existing location app form fields (new assistant being created)
        // 2. The selected form fields are currently empty
        const shouldApplyDefaults = props.locationAppFormFields.length === 0 && props.values.selectedFormFields.length === 0;

        if (shouldApplyDefaults) {
          const defaultFieldTitles = getDefaultFormFieldsByAssistantType(props.values.assistantType);

          // Find matching form fields from the sections
          const allFormFields = newSections.flatMap(section => section.templateFormFields);
          const defaultFormFields = allFormFields.filter(field =>
            defaultFieldTitles.includes(field.title)
          );

          if (defaultFormFields.length > 0) {
            props.setFieldValue("selectedFormFields", defaultFormFields);
          }
        }

        setActiveIndex(0);
      } else {
        setFilteredSections([]);
      }
    };

    updateSectionsForAssistantType();
  }, [props.values.assistantType]);

  useEffect(() => {
    // set initial selectedFormFields value, since it's not set in time by the mapPropsToValues Formik function
    if (props.commonApp) {
      props.setFieldValue("commonAppName", props.commonApp?.name);
    }
    if (props.locationAppFormFields.length > 0) {
      const locationAppFormFieldsCopy = [...props.locationAppFormFields];
      for (const locationAppFormField of locationAppFormFieldsCopy) {
        const commonAppFormField = props.commonAppSections
          .flatMap((section) => section.templateFormFields)
          .find((formField) => formField.title === locationAppFormField.title);
        if (commonAppFormField) {
          locationAppFormField.id = commonAppFormField.id;
        }
      }
      props.setFieldValue("selectedFormFields", locationAppFormFieldsCopy);
    }
    if (props.locationAppNames.length > 0) {
      props.setFieldValue("locationNames", props.locationAppNames);
    } else {
      props.setFieldValue("locationNames", [""]);
    }
  }, [props.locationAppFormFields, props.locationAppNames, props.commonApp?.name]);

  useEffect(() => {
    const deselectedFormFields = originalSelectedFormFields?.filter((originalFormField) => {
      return !props.values.selectedFormFields.some((selectedFormField) => selectedFormField.title === originalFormField.title);
    });
    props.setFieldValue("deselectedFormFields", deselectedFormFields);
  }, [props.values.selectedFormFields]);

  // Effect to handle submission results from props or form validation
  useEffect(() => {
    // Check if there are any Formik errors that should be displayed
    if (isSubmitted && !props.isSubmittingForm) {
      // Check for status results (from handleSubmit)
      if (props.status?.type === 'error') {
        const statusErrors = [props.status.message];
        if (props.status.details && props.status.details.length > 0) {
          statusErrors.push(...props.status.details);
        }
        handleFormSubmissionResult(false, statusErrors);
        return;
      }

      if (props.status?.type === 'success') {
        handleFormSubmissionResult(true);
        return;
      }

      // Check for field validation errors
      const formErrors = Object.keys(props.errors).length > 0 ? Object.values(props.errors) : [];
      if (formErrors.length > 0) {
        handleFormSubmissionResult(false, formErrors as string[]);
      }
      // Don't auto-set success anymore - wait for explicit success status
    }
  }, [isSubmitted, props.isSubmittingForm, props.errors, props.status]);

  const createFilteredSections = (search: string) => {
    if (search?.length) {
      const searchLower = search.toLowerCase();
      const sectionsToFilter = filteredSections.length > 0 ? filteredSections : props.commonAppSections;
      const searchFilteredSections = sectionsToFilter
        .map((section) => {
          const filteredFields = section.templateFormFields.filter(
            (formField) => formField.title.toLowerCase().includes(searchLower) || section.title.toLowerCase().includes(searchLower)
          );
          return { ...section, templateFormFields: filteredFields };
        })
        .filter((section) => section.templateFormFields.length > 0);

      setFilteredSections(searchFilteredSections);
    } else {
      // If no search term, get sections for current assistant type
      if (props.values.assistantType) {
        getSectionsForAssistantType(props.values.assistantType).then(sections => {
          setFilteredSections(sections);
        });
      } else {
        setFilteredSections([]);
      }
    }
  };

  const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement>, fieldName: string) => {
    return props.setFieldValue(fieldName, e.target.value);
  };

  const handleAssistantTypeChange = (selectedOption: any, value: string) => {
    // If user manually changes assistant type and there are no existing locationAppFormFields,
    // we can clear the selected form fields to start fresh
    const shouldClearSelections = props.locationAppFormFields.length === 0 && props.values.selectedFormFields.length > 0;

    props.setFieldValue("assistantType", value);

    if (shouldClearSelections) {
      props.setFieldValue("selectedFormFields", []);
    }

    // Clear PMS Name if changing away from dental scheduling
    if (value !== 'dental-scheduling') {
      props.setFieldValue("pmsName", undefined);
    }
  };

  const handlePmsNameChange = (selectedOption: any, value: string) => {
    props.setFieldValue("pmsName", value);
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>, commonAppFormField: TemplateFormFieldModel) => {
    const selectedFormFieldsCopy = [...props.values.selectedFormFields];
    if (e.target.checked) {
      // add form field to selected form fields if not already there
      if (
        selectedFormFieldsCopy.some(
          (selectedFormField) => selectedFormField.id === commonAppFormField.id
        )
      ) {
        // do nothing if already checked
        return;
      } else {
        // Look in both filtered sections and original common app sections to handle all cases
        const filteredFormFields = filteredSections.flatMap(
          (section) => section.templateFormFields
        );
        const commonAppFormFields = props.commonAppSections.flatMap(
          (section) => section.templateFormFields
        );

        // Try to find the field in filtered sections first, then fall back to common app sections
        let selectedFormFieldToCheck = filteredFormFields.find(
          (formField) => formField.id === commonAppFormField.id || formField.title === commonAppFormField.title
        );

        if (!selectedFormFieldToCheck) {
          selectedFormFieldToCheck = commonAppFormFields.find(
            (formField) => formField.id === commonAppFormField.id || formField.title === commonAppFormField.title
          );
        }

        if (selectedFormFieldToCheck) {
          selectedFormFieldsCopy.push(selectedFormFieldToCheck);
        }
      }
    } else {
      // remove form field from selected form fields if it is there
      const selectedFormFieldToUncheck = selectedFormFieldsCopy.find(
        (selectedFormField) => selectedFormField.id === commonAppFormField.id || selectedFormField.title === commonAppFormField.title
      );

      if (selectedFormFieldToUncheck) {
        selectedFormFieldsCopy.splice(
          selectedFormFieldsCopy.indexOf(selectedFormFieldToUncheck),
          1
        );
      }
    }
    props.setFieldValue("selectedFormFields", selectedFormFieldsCopy);
  };

  const renderSectionFormFields = (commonAppSection: TemplateFormSectionModel) => {
    if (commonAppSection.templateFormFields.length > 0) {
      return commonAppSection?.templateFormFields?.map((commonAppFormField) => {
        return (
          <CheckboxField
            label={commonAppFormField.title}
            key={commonAppFormField.id}
            name={commonAppFormField.title}
            checked={props.values.selectedFormFields.some(
              (selectedFormField) =>
                selectedFormField.title === commonAppFormField.title ||
                selectedFormField.id === commonAppFormField.id
            )} //checking both titles and ids for flexibility
            onChange={(e) => handleCheckboxChange(e, commonAppFormField)}
            className={checkboxField}
            disabled={props.isLoadingTemplates}
            tooltip={commonAppFormField?.label}
            tooltipPosition={"right"}
            tooltipStyle={tooltipStyle}
            tooltipContentStyle={tooltipContentStyle}
          />
        );
      });
    }
  };

  const handleNextButtonClick = () => {
    if (activeIndex < filteredSections.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const handlePreviousButtonClick = () => {
    if (activeIndex <= filteredSections.length - 1) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleSubmitButtonClick = () => {
    // Clear previous errors and success status
    setSubmissionErrors([]);
    setSubmissionSuccess(false);
    setIsSubmitted(true);
    // Don't set success here - wait for actual API results
  };

  const handleFormSubmissionResult = (success: boolean, errors: string[] = []) => {
    if (success) {
      setSubmissionSuccess(true);
      setSubmissionErrors([]);
    } else {
      setSubmissionSuccess(false);
      setSubmissionErrors(errors.length > 0 ? errors : ["An unexpected error occurred during form submission."]);
    }
  };

  const resetFormSubmissionState = () => {
    setIsSubmitted(false);
    setSubmissionSuccess(false);
    setSubmissionErrors([]);
  };

  const handleSearch = (searchEvent: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(timer);
    setSearch(searchEvent.target.value);
    timer = setTimeout(
      () => createFilteredSections(searchEvent.target.value),
      WAIT_INTERVAL
    );
    timer = setTimeout(() => setActiveIndex(0), WAIT_INTERVAL);
  };

  const clearSearchTerm = () => {
    setSearch("");
    setActiveIndex(0);
    // Restore sections for current assistant type
    if (props.values.assistantType) {
      getSectionsForAssistantType(props.values.assistantType).then(sections => {
        setFilteredSections(sections);
      });
    } else {
      setFilteredSections([]);
    }
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      clearSearchTerm();
    }
  };

  const renderLocationButtons = (index: number) => {
    return (
      <SearchButtonsWrapper>
        <div>
          <VerticalSeparator className={separatorStyle} />
        </div>

        <div>
          <StyledSearchField
            placeholder={"Search All Topics"}
            boxType={"rounded"}
            onChange={handleSearch}
            onKeyDown={handleSearchKeyDown}
            searchIcon={searchIcon}
            value={search}
            deletePosition={"right"}
            deleteIcon={xIcon}
            handleClear={clearSearchTerm}
          />
        </div>

        {index !== filteredSections.length - 1 ? (
          <ButtonWrapper>
            <StyledButton
              className={index === 0 && lightButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                (index !== 0 ? handlePreviousButtonClick() : e.stopPropagation())
              }
              }
              themes={["icon"]}
              icon={index !== 0 ? leftArrow : lightLeftArrow}
              disabled={index === 0}
            />
            <StyledButton
              onClick={(e) => { handleNextButtonClick(); e.stopPropagation(); }}
              themes={["icon"]}
              icon={filteredSections.length > 0 ? rightArrow : lightRightArrow}
              disabled={filteredSections.length === 0}
            />
          </ButtonWrapper>
        ) : (
          <ButtonWrapper>
            <StyledButton
              className={filteredSections.length === 1 && lightButtonStyle}
              onClick={(e) => {
                filteredSections.length !== 1
                  ? handlePreviousButtonClick()
                  : null
                e.stopPropagation();
              }}
              themes={["icon"]}
              icon={filteredSections.length !== 1 ? leftArrow : lightLeftArrow}
              disabled={filteredSections.length === 1}
            />
            <StyledButton
              className={lightButtonStyle}
              onClick={(e) => e.stopPropagation()}
              disabled={true}
              themes={["icon"]}
              icon={lightRightArrow}
            />
          </ButtonWrapper>
        )}
      </SearchButtonsWrapper>
    );
  };

  const renderSubmitButton = () => {
    // If form is submitted and there are errors, show Try Again and Go to Dashboard buttons
    if (isSubmitted && submissionErrors.length > 0) {
      return (
        <SubmitButtonWrapper>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <StyledButton
              onClick={resetFormSubmissionState}
              themes={["secondary"]}
              text="Try Again"
              type="button"
            />
            <StyledButton
              onClick={() => props.history.push("/")}
              themes={["flat"]}
              text="Go to Dashboard"
              type="button"
            />
          </div>
        </SubmitButtonWrapper>
      );
    }

    // If form is submitted and successful, show Go to Dashboard button
    if (isSubmitted && submissionSuccess) {
      return (
        <SubmitButtonWrapper>
          <StyledButton
            onClick={() => props.history.push("/")}
            themes={["primary"]}
            text="Go to Dashboard"
            type="button"
          />
        </SubmitButtonWrapper>
      );
    }

    // Default state: show Submit All button
    return (
      <SubmitButtonWrapper>
        <StyledButton
          onClick={() => {
            handleSubmitButtonClick();
            props.handleSubmit();
          }}
          loading={props.isSubmittingForm}
          themes={["primary"]}
          text="Submit All"
          type="submit"
        />
      </SubmitButtonWrapper>
    );
  };

  const handleAddLocationButtonClick = (arrayHelpers: FieldArrayRenderProps) => {
    arrayHelpers.push("");
    isLocationCollapsed && setIsLocationCollapsed(!isLocationCollapsed);
  };

  const handleAddLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const renderAddButton = (arrayHelpers: FieldArrayRenderProps) => {
    return (
      <AddButtonWrapper>
        <Button
          themes={["flat"]}
          text="+ Add a Location"
          type="button"
          className={addLocationName}
          onClick={() => handleAddLocationButtonClick(arrayHelpers)}
        />
      </AddButtonWrapper>
    );
  };

  const renderNoFormsMessage = () => {
    const assistantTypeLabel = assistantTypeOptions.find(option => option.value === props.values.assistantType)?.label || 'this assistant type';

    return (
      <div className={noFormsMessageWrapper}>
        <p className={noMessageStyle}>
          {search ?
            "Sorry, that topic doesn't exist!" :
            `No topics available for ${assistantTypeLabel}. This module may not be configured yet.`
          }
        </p>
        <p className={xMessageStyle}>
          {search ?
            "Click x to clear the search field." :
            "Please contact support if you expected to see topics here."
          }
        </p>
      </div>
    );
  };

  return (
    <form>
      <AssistantTypeWrapper hasAssistantType={!!props.values.assistantType}>
        <CustomDropdown
          label="Assistant Type"
          labelStyle={assistantTypeLabelStyle}
          handleOnChange={handleAssistantTypeChange}
          options={assistantTypeOptions}
          value={props.values.assistantType}
          placeholder="Select assistant type"
          size="medium"
          disabled={props.isLoadingTemplates || isAssistantTypeDisabled}
        />
        {props.errors.assistantType && props.touched.assistantType && (
          <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
            {props.errors.assistantType}
          </div>
        )}

        {props.values.assistantType === 'dental-scheduling' && (
          <>
            <CustomDropdown
              label="PMS Name"
              labelStyle={assistantTypeLabelStyle}
              handleOnChange={handlePmsNameChange}
              options={pmsNameOptions}
              value={props.values.pmsName}
              placeholder="Select PMS name"
              size="medium"
              disabled={props.isLoadingTemplates}
            />
            {props.errors.pmsName && props.touched.pmsName && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                {props.errors.pmsName}
              </div>
            )}
          </>
        )}
      </AssistantTypeWrapper>

      {props.values.assistantType && (
        <>
          <CollapsingPanel
            isCollapsed={isLocationCollapsed}
            collapsedView={
              <FieldArray
                validateOnChange={false}
                name="locationNames"
                render={(arrayHelpers) => (
                  <LocationsPanelWrapper>
                    <div
                      onClick={() =>
                        setIsLocationCollapsed(!isLocationCollapsed)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <TextField
                        className={labelStyle}
                        fieldContainerStyle={outerFieldContainerStyle}
                        name="Locations"
                        disabled={props.isLoadingTemplates}
                        required={false}
                        value={"View Locations"}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        icon={expandIcon}
                        iconPosition="right"
                        fieldStyle={textFieldStyle}
                      />
                    </div>

                    {renderAddButton(arrayHelpers)}
                  </LocationsPanelWrapper>
                )}
              />
            }
            expandedView={
              <FieldArray
                validateOnChange={false}
                name="locationNames"
                render={(arrayHelpers) => (
                  <LocationsPanelWrapper>
                    <div
                      onClick={() =>
                        setIsLocationCollapsed(!isLocationCollapsed)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <TextField
                        className={labelStyle}
                        fieldContainerStyle={outerFieldContainerStyle}
                        name="Locations"
                        disabled={props.isLoadingTemplates}
                        required={false}
                        value={"Collapse All Locations"}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        icon={collapseIcon}
                        iconPosition="right"
                        fieldStyle={textFieldStyle}
                      />
                    </div>

                    {props.values.locationNames.map((locationName, index) => {
                      return (
                        <TextFieldWithDeleteWrapper
                          key={`locationNames.${index}`}
                        >
                          <TextField
                            name={`locationNames.${index}`}
                            disabled={props.locationApps?.some(
                              (l) => l.name === locationName
                            )}
                            value={locationName}
                            label=""
                            placeholder="Type a location name here"
                            required={true}
                            onChange={(e) =>
                              handleTextFieldChange(e, `locationNames.${index}`)
                            }
                            onBlur={props.handleBlur}
                            fieldContainerStyle={fieldContainerStyle}
                            className={textFieldContainerStyle}
                            autoFocus={true}
                            onKeyDown={(e) => handleAddLocationKeyDown(e)}
                          />

                          {!props.locationApps.some(
                            (locationApp) => locationApp.name === locationName
                          ) && (
                              <DeleteIcon
                                src={deleteIcon}
                                onClick={() => arrayHelpers.remove(index)}
                              />
                            )}
                        </TextFieldWithDeleteWrapper>
                      );
                    })}

                    {renderAddButton(arrayHelpers)}
                  </LocationsPanelWrapper>
                )}
              />
            }
          />

          <OutsideFormWrapper>
            <div className={OutsideFormHeader}>
              <CheckBoxSectionsTitle>
                Location-Specific Topics
              </CheckBoxSectionsTitle>

              <CheckBoxSectionsDescription>
                Select the form field topic(s) that will have different answers
                per location.
              </CheckBoxSectionsDescription>
            </div>

            <FieldArray
              name="selectedFormFields"
              render={() => {
                return (
                  <CheckboxFieldWrapper>
                    {!isSubmitted ? (
                      Array.isArray(filteredSections) &&
                        filteredSections.length > 0 ? (
                        filteredSections.map((section, index) => {
                          if (activeIndex === index && activeIndex !== null) {
                            return (
                              <div key={index}>
                                <InsideFormWrapper>
                                  <SectionWrapper
                                    key={`${section.title}.${index}`}
                                  >
                                    <SectionTitle>
                                      <InsideFormHeader>
                                        <div>{section.title}</div>
                                        {renderLocationButtons(index)}
                                      </InsideFormHeader>
                                    </SectionTitle>
                                    {renderSectionFormFields(section)}
                                    <PageNumberWrapper>
                                      <p>
                                        {activeIndex + 1} /{" "}
                                        {filteredSections.length}
                                      </p>
                                    </PageNumberWrapper>
                                  </SectionWrapper>
                                </InsideFormWrapper>
                                {renderSubmitButton()}
                              </div>
                            );
                          }
                          return null;
                        })
                      ) : (
                        <>
                          <InsideFormWrapper>
                            <SectionWrapper key={``}>
                              <SectionTitle>
                                <InsideFormHeader>
                                  {renderNoFormsMessage()}
                                  {renderLocationButtons(activeIndex)}
                                </InsideFormHeader>
                              </SectionTitle>
                            </SectionWrapper>
                          </InsideFormWrapper>
                          {renderSubmitButton()}
                        </>
                      )
                    ) : (
                      <ThankYouWrapper>
                        {submissionErrors.length > 0 ? (
                          <>
                            <TextField
                              name="SubmissionError"
                              disabled={props.isLoadingTemplates}
                              required={false}
                              value="There was an error building or updating your assistants. Please check the details below and try again."
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              icon={checkIcon}
                              iconPosition="right"
                            />
                            <PageError
                              errors={submissionErrors}
                              onClear={() => setSubmissionErrors([])}
                              className="form-submission-error"
                            />
                          </>
                        ) : submissionSuccess ? (
                          <>
                            <TextField
                              name="Submitted"
                              disabled={props.isLoadingTemplates}
                              required={false}
                              value="Your assistants have been successfully created and configured."
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              icon={checkIcon}
                              iconPosition="right"
                            />
                          </>
                        ) : (
                          <>
                            <TextField
                              name="Processing"
                              disabled={props.isLoadingTemplates}
                              required={false}
                              value="Your assistants are being created and configured."
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              icon={checkIcon}
                              iconPosition="right"
                            />
                            <LoaderWrapper>
                              <SecondaryLoader />
                            </LoaderWrapper>
                          </>
                        )}
                        {renderSubmitButton()}
                      </ThankYouWrapper>
                    )}
                  </CheckboxFieldWrapper>
                );
              }}
            />
          </OutsideFormWrapper>
        </>
      )}
    </form>
  );
};

const assistantTypeLabelStyle = css`
    font-size: 14px;
    color: ${dark_grey_blue};
    font-weight: bold;
    margin: 4px 0 4px 22px;
`;

const tooltipStyle = css`
  width: 20px;
  height: 20px;
  margin-top: 2px;
  margin-left: 8px;
  border: solid 1px ${text_blue};
  span {
    color: ${text_blue};
  }
`;

const tooltipContentStyle = css`
  .rc-tooltip-inner {
    font-family: Muli;
    font-size: 14px;
    font-weight: normal;
    font-style: normal;
    letter-spacing: 0.6px;
    z-index: 120;
    color: ${color_shades_darker};
    background-color: white;
    border: 1px solid ${color_shades_dark};
    padding: 8px;
    min-height: 0;
    box-shadow: -1px 4px 10px 0 rgba(48, 69, 98, 0.1);
  }
`;

const labelStyle = css`
  padding: 0px 0px;
`;

const textFieldStyle = css`
  cursor: pointer;
  color: ${dark_grey_blue};
  font-family: Muli;
  font-weight: bold;
  text-align: left;
  width: 100%;
  padding: 12px 42px;
  font-size: 14px;
  background: transparent;
  border: none;
`;

const separatorStyle = css`
  width: 1px;
  height: 30px;
  color: ${silver_two}
  ${breakpoint_small} {
    display: none;
  }
`;

const CheckBoxSectionsDescription = styled.p`
  font-size: 14px;
  color: ${color_shades_darker};
  margin: 0px 0px 16px 34px;
`;

const OutsideFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
`;

const InsideFormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${breakpoint_small} {
    margin: 32px 0;
  }
`;

const SearchButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  img {
    margin: 0px;
  }
  ${breakpoint_small} {
    margin: 8px 0;
  }
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 32px;
  &:first-of-type {
    margin-left: 16px;
  }
`;

const OutsideFormHeader = css`
  margin-left: 8px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  margin-top: -4px;
  margin-left: 16px;
  margin-bottom: 0px;
`;

const CheckBoxSectionsTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin: -32px 32px 8px 34px;
`;

const CheckboxFieldWrapper = styled.div`
  padding: 0px 28px 32px 28px;
  ${breakpoint_small} {
    padding: 8px;
  }
`;

const checkboxField = css`
  .check-label {
    font-weight: 500;
  }
  margin-left: 16px;
`;

const fieldContainerStyle = css``;

const outerFieldContainerStyle = css`
  width: 100%;
  display: flex;
  border-bottom: solid 1px ${color_shades_dark};
  align-items: center;
  height: 60px;
  border-radius: 0px;
  border: none;
  border-top: solid 1px ${color_shades_dark};
  border-bottom: solid 1px ${color_shades_dark};
  background: white;
  margin: 16px 0px -30px 0px;
  padding-right: 29px;
`;

const textFieldContainerStyle = css`
  margin-bottom: 0px;
`;

const DeleteIcon = styled.img`
  cursor: pointer;
  margin: 32px 12px 12px 12px;
`;

const PageNumberWrapper = styled.div`
  display: flex;
  justify-content: right;
  font-size: 12px;
  color: ${silver_two};
  margin: -4px 16px 16px 0px;
`;

const TextFieldWithDeleteWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 32px 0px 32px;
  &:nth-child(n+2) {
    margin-top: 16px;
  }
  &:nth-child(2) {
    margin-top: 32px;
  }
`;

const LocationsPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 26px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: right;
  margin-top: 0px;
  padding-bottom: 0px;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  margin: 48px 0px 24px -6px;
  ${breakpoint_small} {
    justify-content: flex-end;
  }
`;

const InsideFormWrapper = styled.div`
  background: white;
  border: 1px solid ${color_shades_dark};
  box-sizing: border-box;
  border-radius: 8px;
  padding: 0px 0px 0px 2px;
  margin: 0px 0px 0px 0px;
`;

const ThankYouWrapper = styled.div`
  margin-top: -10px;
  position: relative;
  
  .form-submission-error {
    position: relative;
    right: auto;
    bottom: auto;
    width: 100%;
    margin-top: 16px;
    border-radius: 8px;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  margin: 16px 0;
`;

const lightButtonStyle = css`
  cursor: not-allowed;
`;

const addLocationName = css`
    color: ${text_blue}
    font-size: 14px;
    box-shadow: none;
`;

const noFormsMessageWrapper = css`
  display: flex;
  flex-direction: column;
`;

const noMessageStyle = css`
  font-size: 14px;
  padding-top: 2px;
`;

const xMessageStyle = css`
  padding-top: 6px;
  color: ${color_shades_dark};
  font-style: italic;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: right;
  margin: -10px -12px 0px 0px;
`;

const StyledButton = styled(Button)`
  margin: 0px 9px 0px 6px;
`;

const StyledSearchField = styled(SearchField)`
  padding: 16px 16px 0px 20px;
  .field-container {
    height: 26px;
    border: solid 1.2px ${silver_two};
    box-shadow: 0 0 0 0;
    margin-top: 0 px;
    img {
      margin-left: 8px;
    }
  }
  input {
    font: Muli;
    color: ${color_shades_darkest};
    font-weight: normal;
    letter-spacing: 1px;
    ::placeholder {
      font: Muli;
      font-size: 12px;
      font-weight: normal;
      font-style: italic;
      letter-spacing: 1px;
      color: ${silver_two};
    }
  }
  ${breakpoint_small} {
    display: none;
  }
`;

const CustomDropdown = styled(StyledDropdown)`
    padding: 5px;
    margin: 0 0 24px 0;
    
    .Select-menu-outer {
        max-height: none !important;
        z-index: 10001;
        padding: 10px 0px;
    }
    
    .Select-menu {
        max-height: 300px;
    }
`;

const AssistantTypeWrapper = styled.div<{ hasAssistantType: boolean }>`
    padding: 16px 16px 0 16px;
    ${props => !props.hasAssistantType ? 'height: calc(100vh - 325px);' : ''}
`;

export default SparkLocationsForm;
